'use client';

import { type ChangeEvent, useState, useTransition } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import type { ContactFormLabels } from '../_content';
import {
  CONTACT_TOPICS,
  type ContactInput,
  ContactSchema,
  type ContactTopic,
  MAX_ATTACHMENTS_TOTAL_BYTES,
  MAX_ATTACHMENT_BYTES,
  MAX_ATTACHMENT_COUNT,
} from '../_lib/contact.schema';
import { sendContactMessage } from '../_lib/server/server-actions';

/**
 * Kontaktformular – Prototyp-Markup (.locari-form/.field/.hp-field/.form-success)
 * verdrahtet mit react-hook-form + Zod gegen die Server-Action mit Topic-Routing.
 * (Bewusst NICHT @kit/ui/form: das Marketing nutzt sein eigenes Form-Design.)
 *
 * Lokalisierung: das Client-Child bleibt „dumm" – alle sichtbaren Texte kommen
 * via `labels` + `topicLabels` aus dem Server-Parent (ContactPage liest
 * contactContent[locale]). Die Topic-KEYS bleiben unverändert (Server-Routing);
 * nur die Anzeige-Labels sind lokalisiert.
 */
export function ContactForm({
  initialTopic,
  labels,
  topicLabels,
}: {
  initialTopic?: string;
  labels: ContactFormLabels;
  topicLabels: Record<ContactTopic, string>;
}) {
  const validInitial =
    initialTopic && (CONTACT_TOPICS as readonly string[]).includes(initialTopic)
      ? (initialTopic as ContactTopic)
      : undefined;

  const [submitted, setSubmitted] = useState(false);
  const [pending, startTransition] = useTransition();

  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm<ContactInput>({
    resolver: zodResolver(ContactSchema),
    defaultValues: {
      topic: validInitial as ContactTopic,
      name: '',
      email: '',
      message: '',
      company: '',
    },
  });

  const topicValue = watch('topic');

  // Anhänge als echte File-Objekte halten → gehen binär per FormData an die
  // Server-Action (kein Base64, kein 413 am Vercel-Ingress).
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);

  const onFilesChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFileError(null);
    const picked = Array.from(event.target.files ?? []);
    event.target.value = ''; // erlaubt erneutes Wählen derselben Datei
    if (picked.length === 0) return;

    const next = [...files];
    let rejected = false;

    for (const file of picked) {
      const isPdf =
        file.type === 'application/pdf' ||
        file.name.toLowerCase().endsWith('.pdf');
      const total = next.reduce((sum, a) => sum + a.size, 0);

      if (
        !isPdf ||
        file.size > MAX_ATTACHMENT_BYTES ||
        next.length >= MAX_ATTACHMENT_COUNT ||
        total + file.size > MAX_ATTACHMENTS_TOTAL_BYTES
      ) {
        rejected = true;
        continue;
      }

      next.push(file);
    }

    setFiles(next);
    if (rejected) setFileError(labels.uploadError);
  };

  const removeFile = (index: number) => {
    setFiles((current) => current.filter((_, i) => i !== index));
    setFileError(null);
  };

  const onSubmit = handleSubmit((values) => {
    startTransition(async () => {
      try {
        const formData = new FormData();
        formData.set('topic', values.topic);
        formData.set('name', values.name);
        formData.set('email', values.email);
        formData.set('message', values.message);
        formData.set('company', values.company ?? '');
        if (values.topic === 'careers') {
          for (const file of files) formData.append('attachments', file);
        }

        await sendContactMessage(formData);
        setSubmitted(true);
      } catch {
        setError('root', { message: labels.rootError });
      }
    });
  });

  if (submitted) {
    return (
      <div className="form-success is-visible" role="status" aria-live="polite">
        <span className="success-mark" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </span>
        <h3>{labels.successTitle}</h3>
        <p>{labels.successBody}</p>
      </div>
    );
  }

  return (
    <form className="locari-form" onSubmit={onSubmit} noValidate>
      <div className="hp-field" aria-hidden="true">
        <label htmlFor="company">{labels.honeypotLabel}</label>
        <input
          type="text"
          id="company"
          tabIndex={-1}
          autoComplete="off"
          {...register('company')}
        />
      </div>

      <div className="field">
        <label htmlFor="topic">
          <span>{labels.topicLabel}</span>
        </label>
        <select
          id="topic"
          {...register('topic')}
          defaultValue={validInitial ?? ''}
        >
          <option value="" disabled>
            {labels.topicPlaceholder}
          </option>
          {CONTACT_TOPICS.map((topic) => (
            <option key={topic} value={topic}>
              {topicLabels[topic]}
            </option>
          ))}
        </select>
        {errors.topic ? (
          <p className="field-error">{labels.topicError}</p>
        ) : null}
      </div>

      <div className="field">
        <label htmlFor="name">
          <span>{labels.nameLabel}</span>
        </label>
        <input
          type="text"
          id="name"
          autoComplete="name"
          placeholder={labels.namePlaceholder}
          {...register('name')}
        />
        {errors.name ? <p className="field-error">{labels.nameError}</p> : null}
      </div>

      <div className="field">
        <label htmlFor="email">
          <span>{labels.emailLabel}</span>
        </label>
        <input
          type="email"
          id="email"
          autoComplete="email"
          placeholder={labels.emailPlaceholder}
          {...register('email')}
        />
        {errors.email ? (
          <p className="field-error">{labels.emailError}</p>
        ) : null}
      </div>

      <div className="field">
        <label htmlFor="message">
          <span>{labels.messageLabel}</span>
        </label>
        <textarea
          id="message"
          rows={5}
          placeholder={labels.messagePlaceholder}
          {...register('message')}
        />
        {errors.message ? (
          <p className="field-error">{labels.messageError}</p>
        ) : null}
      </div>

      {topicValue === 'careers' ? (
        <div className="field">
          <label htmlFor="attachments">
            <span>{labels.uploadLabel}</span>
          </label>
          <input
            type="file"
            id="attachments"
            accept="application/pdf,.pdf"
            multiple
            onChange={onFilesChange}
          />
          <p className="field-hint">{labels.uploadHint}</p>
          {fileError ? <p className="field-error">{fileError}</p> : null}
          {files.length > 0 ? (
            <ul className="attachment-list">
              {files.map((file, index) => (
                <li key={`${file.name}-${index}`}>
                  <span>
                    {file.name} ({Math.round(file.size / 1024)} KB)
                  </span>
                  <button
                    type="button"
                    className="attachment-remove"
                    onClick={() => removeFile(index)}
                  >
                    {labels.uploadRemove}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      <p className="form-privacy">{labels.privacyNote}</p>

      <div className="submit-row">
        <button
          type="submit"
          className="btn btn-primary btn-lg"
          disabled={pending}
        >
          {pending ? labels.submitting : labels.submit}
        </button>
      </div>

      {errors.root ? (
        <p className="field-error" role="alert">
          {errors.root.message}
        </p>
      ) : null}
    </form>
  );
}
